import { forwardRef, useState } from 'react';
import { Field } from 'formik';
import {
    FormControl,
    Stack,
    FormLabel,
    Box,
    Text,
    Spinner,
} from '@chakra-ui/react';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import walkthroughIds from '../../walkthroughIds';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    dropDownFormStyle,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { Select } from 'chakra-react-select';

const PlanPartnerField = ({
    errors,
    touched,
    values,
    darkMode,
    loadingPartners,
    partnerOptions,
    setSelectedPartnerID,
    currentWholesaler,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const ChakraSelect = forwardRef((props, ref) => (
        <Select selectedOptionColor="brand" ref={ref} {...props} />
    ));
    ChakraSelect.displayName = 'ChakraSelect';

    // Determine if wholesaler is selected (from form value or currentWholesaler for wholesaler admins)
    const effectiveWholesalerID = values?.wholesalerID || currentWholesaler;
    const hasWholesalerInForm = Boolean(
        effectiveWholesalerID && effectiveWholesalerID !== '',
    );

    // Only show loading if we actually have a wholesaler to load partners for
    const shouldShowLoading = loadingPartners && hasWholesalerInForm;

    // Convert partnerOptions to react-select format
    const options = partnerOptions.map((opt) => ({
        value: opt.id,
        label: opt.name,
    }));
    const selectedOption = values?.partnerID
        ? options.find((op) => op.value === values.partnerID) || null
        : null;

    return (
        <FormControl
            isInvalid={!!errors.partnerID && touched.partnerID}
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Partner
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Select the partner for this plan.
                    </Text>
                </Box>
                <Box flex="1" sx={{ paddingTop: '15px' }}>
                    {shouldShowLoading ? (
                        <Spinner size="sm" />
                    ) : (
                        <Field name="partnerID">
                            {({ form }) => (
                                <ChakraSelect
                                    minWidth="410px"
                                    width="100%"
                                    data-walkthroughid={
                                        walkthroughIds.plans.ufPartnerSelect
                                    }
                                    options={hasWholesalerInForm ? options : []}
                                    placeholder={
                                        hasWholesalerInForm
                                            ? '- Select Partner -'
                                            : '- Please select a wholesaler -'
                                    }
                                    menuPortalTarget={
                                        typeof window !== 'undefined'
                                            ? document.body
                                            : undefined
                                    }
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                    getOptionLabel={(i) => i.label}
                                    getOptionValue={(i) => i.value}
                                    chakraStyles={dropDownFormStyle(darkMode)}
                                    isLoading={form.isSubmitting}
                                    isDisabled={
                                        !hasWholesalerInForm ||
                                        form.isSubmitting
                                    }
                                    value={
                                        hasWholesalerInForm
                                            ? selectedOption
                                            : null
                                    }
                                    onChange={(option) => {
                                        const partnerID = option
                                            ? option.value
                                            : '';
                                        form.setFieldValue(
                                            'partnerID',
                                            partnerID,
                                        );

                                        // Update selected partner to trigger rate list query refetch
                                        setSelectedPartnerID(partnerID);

                                        form.validateField('partnerID');
                                    }}
                                    isClearable
                                    onBlur={() => {
                                        form.setFieldTouched('partnerID', true);
                                    }}
                                />
                            )}
                        </Field>
                    )}
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="Select the partner for this plan."
                        ariaLabel="plan-partner-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default PlanPartnerField;
